<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber}}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong> {{ $t('Common.companies') }}
          </div>

          <v-layout class="table__header" align-center>
            <v-flex style="max-width: 50px;" xs1 />
            <v-flex xs3>{{ $t(`${namespace}.table.company`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.table.requester`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.table.approver`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.table.orderedOn`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.table.expectedDate`) }}</v-flex>
            <v-flex class="pl-5" xs2>{{ $t(`${namespace}.table.status`) }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <!-- <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--no-click': item.status !== 'DELETED' }"
                @click="item.status === 'DELETED' ? handleClick($event, item) : null"
              > -->
              <v-list-tile class="table__body__tr">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex style="max-width: 50px;" xs1>
                        <v-tooltip
                          v-if="hasRole('anonymization', 'ROLE_PATCH_LGPD_ANONYMIZATION_CONFIRM') && item.status === 'REQUESTED'"
                          slot="append"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleApproveAnonymization(item)"
                          >
                            <v-icon>event_available</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.approveAnonymization`) }}</span>
                        </v-tooltip>

                        <v-tooltip
                          v-if="hasRole('anonymization', 'ROLE_DELETE_LGPD_ANONYMIZATION') && item.status === 'CONFIRMED' && !hasStartAnonymization(item)"
                          slot="append"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleStopAnonymization(item)"
                          >
                            <v-icon>event_busy</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.stopAnonymization`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis" :title="item.companyName">
                          {{ item.companyName | ellipsis(31) }}
                          <span class="gray-6--text caption text--ellipsis">ID: {{ item.companyId }}</span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.requesterName" class="text--ellipsis" :title="item.requesterName">
                          {{ item.requesterName }}
                        </span>
                        <span v-else class="text--ellipsis" :title="item.requestedBy | getUserName">
                          {{ item.requestedBy | getUserName }}
                        </span>

                        <span class="gray-6--text caption text--ellipsis">{{ item.requestedBy }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.confirmerName" class="text--ellipsis" :title="item.confirmerName">
                          {{ item.confirmerName }}
                        </span>
                        <span v-else class="text--ellipsis" :title="item.confirmedBy | getUserName">
                          {{ item.confirmedBy | getUserName }}
                        </span>

                        <span class="gray-6--text caption text--ellipsis">{{ item.confirmedBy }}</span>
                      </v-flex>

                      <v-flex xs2>
                        {{ item.requestDate ? moment(item.requestDate).format('DD/MM/YYYY') : '-' }}
                      </v-flex>

                      <v-flex xs1>
                        {{ item.schedulingDate ? moment(item.schedulingDate).format('DD/MM/YYYY') : '-' }}
                      </v-flex>

                      <v-flex class="pl-5" xs2>
                        <StatusBullet :status="item.status" />
                        <span
                          v-if="item.status === 'CONFIRMED' && hasStartAnonymization(item)"
                          class="gray-6--text caption text--ellipsis pl-4"
                        >
                          {{ $t('Views.CompanyPage.anonymousCompanies.table.processStarted') }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <ApproveAnonymizationDialog
      :visible="approveAnonymizationDialog"
      :company="selectedItem"
      @close="closeApproveAnonymizationDialog"
    />

    <StopAnonymizationDialog
      :visible="stopAnonymizationDialog"
      :company="selectedItem"
      @close="closeStopAnonymizationDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import StatusBullet from 'components/company/StatusBullet'
import ApproveAnonymizationDialog from 'components/company/anonymousCompanies/dialogs/ApproveAnonymizationDialog'
import StopAnonymizationDialog from 'components/company/anonymousCompanies/dialogs/StopAnonymizationDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    }
  },
  components: {
    EmptyState,
    StatusBullet,
    ApproveAnonymizationDialog,
    StopAnonymizationDialog
  },
  filters: {
    getUserName (email = '') {
      if (!email) return '-'

      const name = email
        .replace(/@.+/, '')
        .replace(/_.+/, '')

      const nameAndSurname = name.split('.').map(str => {
        const [firstLetter, ...tail] = str.split('')
        return firstLetter.toUpperCase() + tail.join('')
      }).join(' ')

      return nameAndSurname
    }
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims,
      isAdmin: state => {
        return state.session.user.business.isAdministrator ||
          state.userClaims.claims.profiles['ROLE_ADMINISTRATOR'] ||
          state.userClaims.claims.profiles['ROLE_FINANCIAL_ADMIN']
      }
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyPage.anonymousCompanies',
      selectedItem: null,
      approveAnonymizationDialog: false,
      stopAnonymizationDialog: false
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-btn') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.$router.push({
          name: 'CompanyDetail',
          params: { id: item.companyId }
        })
      }
    },

    openApproveAnonymizationDialog () {
      this.approveAnonymizationDialog = true
    },

    closeApproveAnonymizationDialog (reload) {
      this.approveAnonymizationDialog = false
      this.selectedItem = null
      reload && this.handleReloadData()
    },

    openStopAnonymizationDialog () {
      this.stopAnonymizationDialog = true
    },

    closeStopAnonymizationDialog (reload) {
      this.stopAnonymizationDialog = false
      this.selectedItem = null
      reload && this.handleReloadData()
    },

    handleApproveAnonymization (company) {
      this.selectedItem = company
      this.openApproveAnonymizationDialog()
    },

    handleStopAnonymization (company) {
      this.selectedItem = company
      this.openStopAnonymizationDialog()
    },

    hasStartAnonymization (item) {
      const diffDays = Math.ceil(this.moment(item.schedulingDate).diff(this.moment(), 'days', true))
      return item.schedulingDate && diffDays <= 0
    }
  }
}
</script>

<style lang="scss">
.table__body__tr--no-click {

  .v-list__tile--link {
    cursor: not-allowed;
  }
}
</style>
